import { ConsentementMeta } from "./components/ConsentementMeta";
import { useCallback, useEffect, useRef, useState } from "react";
import { computeExposure } from "@riseafrica/scoring";
import { CONFIG } from "./config";
import { messageSecondDiagnostic } from "./content/secondDiagnostic";
import { SCAN_LINES_CV, SCAN_LINES_METIER } from "./content/copy";
import {
  DelaiDepasseError,
  MetierInconnuError,
  SecondDiagnosticFermeError,
  TropDeDemandesError,
  enregistrerResultat,
  fetchDiagnostic,
  memoriserParrain,
  type DiagnosticResponse,
} from "./lib/api";
import { parrainDepuisUrl } from "./lib/share";
import { retenirParrain } from "./lib/identite";
import { normalizeMetierPourEvenement } from "./lib/normalize";
import { SID, track } from "./lib/track";
import { AppProvider, useApp, type SelectedTask, type ViewId } from "./state/AppState";
import { sansAnimation } from "./lib/defiler";
import { Footer } from "./components/Footer";
import { Legal } from "./components/Legal";
import { Nav } from "./components/Nav";
import { PayModal } from "./components/PayModal";
import { RappelAchat } from "./components/RappelAchat";
import { Reprise } from "./components/Reprise";
import { StickyCta } from "./components/StickyCta";
import { APropos } from "./screens/APropos";
import { Aide } from "./screens/Aide";
import { Progression } from "./screens/Progression";
import { Attestation, AttestationEntree } from "./screens/Attestation";
import { Home } from "./screens/Home";
import { Barometre } from "./screens/Barometre";
import { Pilotage } from "./screens/Pilotage";
import { Methode } from "./screens/Methode";
import { Dirigeant } from "./screens/Dirigeant";
import { DirigeantFerme } from "./screens/DirigeantFerme";
import { Result1 } from "./screens/Result1";
import { Result2 } from "./screens/Result2";
import { Result3 } from "./screens/Result3";
import { Scan } from "./screens/Scan";
import { Tasks } from "./screens/Tasks";
import { Volet1Start, type Mode1 } from "./screens/Volet1Start";
import { Volet2 } from "./screens/Volet2";
import { Volet3 } from "./screens/Volet3";
import { Outils } from "./screens/Outils";

const ERREUR_METIER_INCONNU =
  "Ce métier n'a pas été reconnu. Reformulez-le, ou passez par l'onglet CV.";
const ERREUR_DEBIT =
  "Trop de demandes depuis cette connexion sur la dernière heure. Réessayez un peu plus tard.";
const ERREUR_CV_COURT =
  "Il faut un peu plus de matière : décrivez au moins vos missions principales, ou utilisez l'exemple.";
const ERREUR_DELAI =
  "L'analyse a mis trop de temps à répondre. Vérifiez votre connexion et relancez.";

/** Traduit une panne d'appel en message lisible pour l'utilisateur. */
function messageErreur(error: unknown): string {
  if (error instanceof DelaiDepasseError) return ERREUR_DELAI;
  if (error instanceof TropDeDemandesError) return ERREUR_DEBIT;
  /*
    Le second diagnostic fermé n'est pas une panne — 31/08/2026.

    Il passe par le même chemin parce que le serveur répond par un refus, mais
    ce qu'il faut afficher est **le résultat précédent** et les deux façons d'en
    obtenir un neuf. Écrire ici « l'analyse n'a pas abouti » ferait passer une
    règle du produit pour une défaillance, et c'est le visiteur qui en
    conclurait que le service est cassé.
  */
  if (error instanceof SecondDiagnosticFermeError) {
    return messageSecondDiagnostic(error.etat) ?? ERREUR_DELAI;
  }
  return `L'analyse n'a pas abouti (${error instanceof Error ? error.message : "erreur inconnue"}). Réessayez.`;
}

/**
 * Retient le parrain de cette personne. C'est le seul point où la boucle de
 * parrainage se referme côté client.
 *
 * Il ne se contente plus de lire l'adresse d'arrivée : `retenirParrain` garde
 * la valeur sur l'appareil, au premier contact seulement, et refuse qu'on soit
 * son propre parrain. Sans cela, un filleul qui arrivait par un lien, regardait,
 * et revenait finir son diagnostic le lendemain n'était plus le filleul de
 * personne — le défaut symétrique de celui du `sid`.
 *
 * L'évènement, lui, n'est émis que pour une **arrivée** portant le paramètre :
 * une valeur relue de l'appareil n'est pas un atterrissage, et la compter comme
 * tel gonflerait `referral_landing` à chaque visite du même filleul.
 */
function useParrainDeLArrivee(): void {
  useEffect(() => {
    const arrivee = parrainDepuisUrl();
    const retenu = retenirParrain(arrivee, SID);
    if (retenu !== null) memoriserParrain(retenu);
    if (arrivee !== null) track("referral_landing", { parrain: arrivee });
  }, []);
}

/**
 * Le lien d'évitement et la région d'annonce, ensemble et partout.
 *
 * ## Ce qui manquait
 *
 * La région `aria-live` — celle qui porte tout ce que `say()` émet — n'était
 * montée que dans le parcours. Hors parcours, `say()` posait donc un message
 * que rien ne rendait, et quatre annonces ne sortaient nulle part : le résultat
 * du diagnostic dirigeant, la confirmation du paiement par l'opérateur, la mise
 * à disposition du rapport, et les issues de la récupération d'un rapport perdu
 * depuis `/aide`.
 *
 * Les deux plus coûteuses portent sur l'achat à 15 000 F : quelqu'un qui navigue
 * au lecteur d'écran payait, et rien ne lui disait que le paiement avait abouti
 * ni que son document existait. La règle non négociable n° 8 exige ces annonces
 * sur les résultats ; elles étaient écrites, elles ne sortaient pas.
 *
 * ## Pourquoi les deux ensemble
 *
 * Ils étaient déjà écrits deux fois, à l'identique, en tête de chaque branche —
 * et c'est précisément ainsi que la seconde copie a perdu la région d'annonce
 * sans que personne ne le voie. Une seule tête, deux emplois.
 */
function TeteAccessible({ liveMessage }: { liveMessage: string }) {
  return (
    <>
      <a className="skip" href="#contenu">
        Aller au contenu principal
      </a>
      {/* Région unique d'annonce : indice obtenu, montant calculé, niveau atteint. */}
      <div className="sr" role="status" aria-live="polite">
        {liveMessage}
      </div>
    </>
  );
}

/**
 * La même tête, sur les pages hors parcours.
 *
 * Elle lit `liveMessage` elle-même : ces pages sont rendues sans le composant
 * qui le tient en main, et le passer en propriété aurait demandé de remonter
 * l'état jusqu'à un endroit qui n'en a pas d'autre usage.
 */
function TeteHorsParcours() {
  const { liveMessage } = useApp();
  return <TeteAccessible liveMessage={liveMessage} />;
}

function Routeur() {
  const { view, goTo, t1, t2, t3, setT1, say, liveMessage, majComparaison } = useApp();
  useParrainDeLArrivee();

  /**
   * Ce que l'écran affichait au moment où une analyse a été lancée.
   *
   * Presser retour pendant les vingt secondes d'analyse ramenait au
   * formulaire — puis la réponse arrivait et l'application repartait de force
   * vers l'écran suivant. L'utilisateur avait repris la main, on la lui
   * reprenait. On ne poursuit donc la navigation que si personne n'a bougé.
   */
  const vueAuLancement = useRef<ViewId | null>(null);

  /*
    La vue courante doit être lue dans une référence, jamais dans la variable
    d'état.

    `lancerMetier` est mémorisé sur des dépendances stables : il est donc créé
    **une seule fois**, au premier rendu, quand la vue vaut « home ». Une
    comparaison écrite sur la variable d'état y restait figée sur cette
    première valeur et rendait donc toujours faux — l'analyse aboutissait, et
    l'application refusait d'avancer. Le visiteur restait sur l'écran d'analyse
    jusqu'à ce qu'il abandonne, sans message et sans issue.

    Le contrôle des points d'entrée ne pouvait pas le voir : il interroge des
    URL, jamais un écran. C'est le parcours joué de bout en bout qui l'a
    attrapé, au premier essai.
  */
  const vueCourante = useRef<ViewId>(view);
  vueCourante.current = view;
  const peutPoursuivre = () =>
    vueAuLancement.current === "s-scan" && vueCourante.current === "s-scan";

  const zoneVue = useRef<HTMLDivElement>(null);
  useEffect(() => {
    // L'accueil s'ouvre par le haut, sans qu'on ait rien déclenché : y déplacer
    // le focus ferait sauter la page dès l'arrivée.
    if (view !== "home") zoneVue.current?.focus({ preventScroll: true });
  }, [view]);

  const [mode, setMode] = useState<Mode1>("job");
  const [job, setJob] = useState("");
  const [cv, setCv] = useState("");
  const [erreur, setErreur] = useState<string | null>(null);
  const [jobInvalide, setJobInvalide] = useState(false);
  const [pack, setPack] = useState<DiagnosticResponse | null>(null);

  /**
   * Enregistre le résultat anonyme et remplace la comparaison provisoire par
   * celle calculée sur les résultats réels du groupe. Silencieux en cas
   * d'échec : l'utilisateur a déjà son indice à l'écran.
   */
  const publierResultat = useCallback(
    (exposure: ReturnType<typeof computeExposure>, mode: "metier" | "cv", args: {
      metierNormalise: string;
      taches: SelectedTask[];
      promptVersion: string;
      degrade: boolean;
    }) => {
      void enregistrerResultat({
        mode,
        metierNormalise: mode === "metier" ? args.metierNormalise : "",
        groupe: exposure.groupe,
        score: exposure.score,
        niveau: exposure.gradient.niveau,
        /*
          Le libellé accompagne les heures — en mode métier seulement.

          Sans lui, « 8 h » ne dit pas 8 h de quoi : le rapprochement se faisait
          par la position dans le pack en cache, qui se régénère tous les
          quatre-vingt-dix jours et à chaque changement de `promptVersion`. La
          série perdait donc son sens en quelques mois, au moment précis où elle
          commence à valoir quelque chose.

          En mode CV, rien n'est transmis : ces libellés-là viennent du texte de
          la personne, et la règle n° 4 l'interdit sans exception.
        */
        taches: args.taches.map((t) => ({
          ...(mode === "metier" ? { texte: t.texte } : {}),
          niveau: t.niveau,
          heures: t.heures ?? 0,
        })),
        scoringVersion: exposure.scoringVersion,
        promptVersion: args.promptVersion,
        degrade: args.degrade,
      }).then((comparaison) => {
        if (comparaison) majComparaison(comparaison);
      });
    },
    [majComparaison],
  );

  /** Traduit une panne d'appel en message lisible, sur l'écran de saisie. */
  const echouer = useCallback(
    (message: string) => {
      // Le message n'a de sens que pour qui attend encore : on le montre à
      // celui qui est resté sur l'écran d'analyse, et on l'abandonne avec la
      // tentative pour celui qui est parti. Mis en réserve, il accueillait la
      // personne à sa visite suivante de l'écran de saisie et lui reprochait
      // un essai qu'elle avait elle-même abandonné.
      if (!peutPoursuivre()) return;
      setErreur(message);
      // Sans cette annonce, la région vocale restait sur « Analyse en cours »
      // après trente secondes d'attente : l'échec n'existait pas à l'oreille.
      say(message);
      goTo("s-t1");
    },
    [goTo, say],
  );

  /** Volet 01, mode métier : le résultat sert d'abord à faire déclarer le temps. */
  const lancerMetier = useCallback(
    async (metier: string) => {
      setErreur(null);
      setJobInvalide(false);
      setMode("job");
      setJob(metier);
      track("diagnostic_start", { mode: "metier", metier: normalizeMetierPourEvenement(metier) });
      track("metier_lance", {});
      goTo("s-scan");
      vueAuLancement.current = "s-scan";
      say("Analyse en cours — comptez une trentaine de secondes, ne fermez pas la page.");

      try {
        const reponse = await fetchDiagnostic({ mode: "metier", metier });
        if (reponse.cacheHit) track("cache_hit", { metier: normalizeMetierPourEvenement(metier) });
        if (reponse.degraded) track("model_fallback", { mode: "metier" });
        else track("model_ok", { mode: "metier" });
        setPack(reponse);
        // L'utilisateur est parti pendant l'analyse : on garde le résultat en
        // mémoire, on ne le ramène pas de force.
        if (!peutPoursuivre()) return;
        goTo("s-tasks");
        // Compté seulement pour qui est encore là : celui qui est parti pendant
        // l'attente est précisément ce que l'écart avec `metier_lance` mesure.
        track("taches_affichees", {});
        say(`${reponse.metier} — indiquez le temps par tâche`);
      } catch (error) {
        track("metier_erreur", { motif: error instanceof MetierInconnuError ? "inconnu" : "panne" });
        if (error instanceof MetierInconnuError) return echouer(ERREUR_METIER_INCONNU);
        return echouer(messageErreur(error));
      }
    },
    [echouer, goTo, say],
  );

  /** Volet 01, mode CV : le résultat mène directement à l'indice. */
  const lancerCv = useCallback(async () => {
    const texte = cv.slice(0, CONFIG.cvMaxChars).trim();
    if (texte.length < 60) {
      setErreur(ERREUR_CV_COURT);
      return;
    }
    setErreur(null);
    track("diagnostic_start", { mode: "cv" });
    goTo("s-scan");
    vueAuLancement.current = "s-scan";
    say("Analyse en cours — comptez une trentaine de secondes, ne fermez pas la page.");

    try {
      const reponse = await fetchDiagnostic({ mode: "cv", cv: texte });
      if (reponse.degraded) track("model_fallback", { mode: "cv" });
      else track("model_ok", { mode: "cv" });

      // En mode CV, toutes les tâches pèsent 1 : aucun temps n'est déclaré.
      const taches: SelectedTask[] = reponse.taches.map((t) => ({ ...t }));
      const exposure = computeExposure({ groupe: reponse.groupe, taches });
      if (exposure.groupFallback) track("scoring_group_fallback", { groupe: reponse.groupe });

      setT1({
        exposure,
        metier: reponse.metier,
        taches,
        actions: reponse.actions,
        ...(reponse.situations ? { situations: reponse.situations } : {}),
        fromCv: true,
        degraded: reponse.degraded,
        meta: { cacheHit: reponse.cacheHit, mode: "CV analysé" },
      });
      track("score_shown", {
        score: exposure.score,
        groupe: exposure.groupe,
        mode: "cv",
        degraded: reponse.degraded,
      });
      publierResultat(exposure, "cv", {
        metierNormalise: "",
        taches,
        promptVersion: reponse.promptVersion,
        degrade: reponse.degraded,
      });
      if (!peutPoursuivre()) return;
      goTo("s-r1");
    } catch (error) {
      if (error instanceof MetierInconnuError) return echouer(ERREUR_METIER_INCONNU);
      return echouer(messageErreur(error));
    }
  }, [cv, echouer, goTo, say, setT1]);

  /** Volet 01, mode métier : calcul de l'indice une fois le temps déclaré. */
  const calculerIndice = useCallback(
    (heuresParTache: Record<number, number>) => {
      if (!pack) return;
      const taches: SelectedTask[] = pack.taches
        .map((tache, i) => ({ ...tache, heures: heuresParTache[i] ?? 0 }))
        .filter((tache) => (tache.heures ?? 0) > 0);

      const exposure = computeExposure({ groupe: pack.groupe, taches });
      if (exposure.groupFallback) track("scoring_group_fallback", { groupe: pack.groupe });
      track("taches_validees", {});

      setT1({
        exposure,
        metier: pack.metier,
        taches,
        actions: pack.actions,
        ...(pack.situations ? { situations: pack.situations } : {}),
        fromCv: false,
        degraded: pack.degraded,
        meta: { cacheHit: pack.cacheHit, mode: "Métier + tâches cochées" },
      });
      track("score_shown", {
        score: exposure.score,
        groupe: exposure.groupe,
        mode: "metier",
        degraded: pack.degraded,
      });
      publierResultat(exposure, "metier", {
        metierNormalise: pack.metier,
        taches,
        promptVersion: pack.promptVersion,
        degrade: pack.degraded,
      });
      goTo("s-r1");
    },
    [goTo, pack, publierResultat, setT1],
  );

  const demarrerDepuisChamp = () => {
    const valeur = job.trim();
    if (valeur.length < 3) {
      setJobInvalide(true);
      return;
    }
    void lancerMetier(valeur);
  };

  return (
    <>
      <TeteAccessible liveMessage={liveMessage} />

      <Nav />

      {/*
        Le changement de volet ne déplaçait que le défilement. Pour qui navigue
        au clavier, le curseur restait sur le bouton d'un écran qui n'existait
        plus ; pour un lecteur d'écran, rien n'annonçait qu'on avait changé de
        page. Le conteneur reçoit le focus à chaque volet — jamais à l'accueil,
        où l'on arrive par le haut sans avoir rien déclenché.
      */}
      {/*
        `id="contenu"` est ici, et nulle part ailleurs dans le parcours —
        02/09/2026.

        Le lien d'évitement pointe vers `#contenu` depuis toujours. Cette ancre
        n'existait que sur l'accueil : sur les huit autres vues du parcours —
        saisie, analyse, tâches, et les six écrans de volet et de résultat — le
        lien ne menait à rien du tout. Pressé, il ne faisait rien.

        C'est le premier élément qu'atteint la tabulation, et le seul geste que
        le produit offre pour sauter la navigation ; le voir échouer en silence
        est pire que son absence, puisqu'on ne cherche plus d'autre moyen.

        Le conteneur de vue est le bon porteur : il existe sur les neuf vues, il
        est déjà focalisable — c'est lui qui reçoit le focus au changement de
        volet, juste au-dessus — et il commence exactement où commence le
        contenu.
      */}
      <div ref={zoneVue} id="contenu" tabIndex={-1} className="zone-vue">
        {view === "home" && (
          <>
            <Home onStartMetier={(metier) => void lancerMetier(metier)} />
            <Legal />
          </>
        )}

        {view === "s-t1" && (
          <Volet1Start
            mode={mode}
            setMode={(next) => {
              setMode(next);
              setErreur(null);
            }}
            job={job}
            setJob={setJob}
            cv={cv}
            setCv={setCv}
            erreur={erreur}
            jobInvalide={jobInvalide}
            onGoJob={demarrerDepuisChamp}
            onGoCv={() => void lancerCv()}
          />
        )}

        {view === "s-scan" && (
          <Scan
            titre={mode === "cv" ? "Analyse de votre CV…" : "Établissement de vos tâches clés…"}
            lignes={mode === "cv" ? SCAN_LINES_CV : SCAN_LINES_METIER}
          />
        )}

        {view === "s-tasks" &&
          (pack ? (
            <Tasks pack={pack} onScore={calculerIndice} onChangerMetier={() => goTo("s-t1")} />
          ) : (
            <Reprise />
          ))}

        {view === "s-r1" && (t1 ? <Result1 t1={t1} /> : <Reprise />)}
        {view === "s-t2" && <Volet2 />}
        {view === "s-r2" && (t2 ? <Result2 t2={t2} /> : <Reprise />)}
        {view === "s-t3" && <Volet3 />}
        {view === "s-r3" && (t3 ? <Result3 t3={t3} /> : <Reprise />)}
      </div>

      <StickyCta />
      <Footer />
      <PayModal />
      <ConsentementMeta />
    </>
  );
}

/**
 * Routage par chemin, réduit au strict nécessaire.
 *
 * Deux pages sortent du parcours : la méthode et la vérification
 * d'attestation. L'une doit être citable par un lien stable, l'autre est
 * consultée par un tiers qui n'a jamais fait le diagnostic. Toutes les autres
 * adresses retombent sur l'application, dont l'état est porté par `view`.
 *
 * Pas de bibliothèque de routage : deux chemins ne justifient pas une
 * dépendance de plus.
 */
function cheminCourant(): {
  page:
    | "app"
    | "methode"
    | "barometre"
    | "pilotage"
    | "attestation"
    | "dirigeant"
    | "apropos"
    | "outils"
    | "aide"
    | "progression";
  serial: string;
} {
  const chemin = window.location.pathname;
  /*
    L'écran dirigeant vit sous `/dirigeant`, et l'API sous `/entreprise`.

    Les deux ne peuvent pas partager une adresse : une réécriture hosting est
    évaluée **avant** le repli vers index.html, donc une route d'écran portant
    le nom d'une fonction servirait du JSON au lieu de la page — sans erreur, et
    sans que rien ne le signale. `npm run verifier` refuse désormais le
    déploiement quand une réécriture occupe un chemin d'écran.
  */
  if (chemin === "/dirigeant" || chemin === "/dirigeant/") {
    return { page: "dirigeant", serial: "" };
  }
  if (chemin === "/methode" || chemin === "/methode/") return { page: "methode", serial: "" };
  if (chemin === "/outils" || chemin === "/outils/") return { page: "outils", serial: "" };
  // Adresse stable : c'est elle qu'on met en note de bas de page, et elle ne
  // doit plus changer une fois citée.
  if (chemin === "/barometre" || chemin === "/barometre/") {
    return { page: "barometre", serial: "" };
  }
  // Interne, jamais lié depuis le site : ni pied de page, ni plan de site. Le
  // jeton protège la donnée ; ne pas l'annoncer évite d'y attirer l'attention.
  if (chemin === "/pilotage" || chemin === "/pilotage/") {
    return { page: "pilotage", serial: "" };
  }
  const attestation = /^\/attestation\/([A-Za-z0-9-]{1,32})\/?$/.exec(chemin);
  if (attestation) return { page: "attestation", serial: attestation[1].toUpperCase() };
  // Sans numéro : la porte de saisie. Elle manquait — un recruteur à qui l'on
  // montre une attestation n'avait aucun endroit où taper le numéro.
  if (chemin === "/attestation" || chemin === "/attestation/") {
    return { page: "attestation", serial: "" };
  }
  if (chemin === "/a-propos" || chemin === "/a-propos/") return { page: "apropos", serial: "" };
  // Adresse courte et devinable : c'est celle qu'on tape quand quelque chose ne
  // marche pas, et ce n'est pas le moment de chercher.
  if (chemin === "/aide" || chemin === "/aide/") return { page: "aide", serial: "" };
  // La page de progression : le jeton est la clé, comme celui d'un rapport.
  // Jamais liée depuis le site — on y arrive par son rapport ou son rappel.
  const progression = /^\/progression\/([A-Za-z0-9_-]{32})\/?$/.exec(chemin);
  if (progression) return { page: "progression", serial: progression[1] };
  return { page: "app", serial: "" };
}

export function App() {
  const route = cheminCourant();

  /*
    L'ancre de l'adresse n'était jamais honorée — 26/08/2026.

    Le navigateur cherche `#mots` **à l'analyse du document**, c'est-à-dire
    avant que React n'ait rien rendu. À cet instant l'élément n'existe pas :
    il n'y a rien vers quoi défiler, et le navigateur ne réessaie pas. Toute
    adresse portant une ancre déposait donc le lecteur en haut de la page.

    Mesuré au navigateur sur `/methode#mots` : `scrollY` valait zéro à trois
    cents millisecondes comme à deux secondes et demie. Le lien menait à la
    bonne page et le lecteur devait chercher le paragraphe lui-même — sur un
    lien dont le seul intérêt était justement de le lui éviter.

    Le défaut est ancien et vaut pour toutes les ancres du produit ; il s'est vu
    le jour où un lien a été posé sur un titre qu'on venait chercher exprès.

    `prefers-reduced-motion` est respecté : un défilement animé imposé à qui
    demande le contraire est précisément ce que ce réglage refuse.
  */
  useEffect(() => {
    const ancre = window.location.hash.slice(1);
    if (ancre === "") return;
    const cible = document.getElementById(ancre);
    if (cible === null) return;
    const sobre = sansAnimation();

    // Tout de suite : laisser l'écran immobile pendant que les polices
    // arrivent serait revenir au défaut qu'on corrige.
    cible.scrollIntoView({ behavior: sobre ? "auto" : "smooth", block: "start" });

    /*
      Puis une seconde fois, sans animation, une fois les polices posées.

      `display=swap` fait rendre le texte en police de repli d'abord : quand la
      vraie police arrive, tout le texte **au-dessus** de la cible se recompose
      et la page se décale sous le lecteur. Le premier défilement atterrit alors
      à côté — mesuré en intégration continue, le titre arrivait dix pixels
      au-dessus de sa place, donc de nouveau sous la barre.

      Ce défaut est invisible depuis l'environnement de développement : le proxy
      y refuse `fonts.googleapis.com`, `document.fonts.size` vaut zéro, et la
      page est rendue en police de repli du début à la fin. Toute mesure de mise
      en page prise ici l'est sans les vraies polices — c'est la CI qui a vu.

      `behavior: "auto"` et non `smooth` : cette seconde passe est une
      correction de quelques pixels, pas un déplacement. L'animer donnerait
      l'impression que la page bouge toute seule.

      Le plafond de trois secondes suit la logique de `policesPretes` dans
      `carte.ts` : une police qui n'arrive jamais ne doit rien bloquer.
    */
    if (!("fonts" in document)) return;
    let vivant = true;
    void Promise.race([
      document.fonts.ready,
      new Promise<unknown>((ok) => window.setTimeout(ok, 3000)),
    ]).then(() => {
      if (vivant) cible.scrollIntoView({ behavior: "auto", block: "start" });
    });
    return () => {
      vivant = false;
    };
  }, []);

  // `Nav` et `Footer` lisent l'état applicatif : les pages hors parcours
  // restent donc dans le fournisseur, même si elles n'utilisent pas `view`.
  if (route.page !== "app") {
    return (
      <AppProvider price={CONFIG.price}>
        <TeteHorsParcours />
        <Nav />
        {route.page === "dirigeant" ? (
          <>
            {/*
              Le même rappel qu'à l'accueil : un dirigeant qui revient chercher
              son rapport revient sur `/dirigeant`, pas sur `/` — sans ce
              bandeau, il devait refaire tout le questionnaire pour retrouver
              le bouton de reprise, qui ne vit que sur l'écran de résultat.
            */}
            <RappelAchat />
            <PageDirigeant />
          </>
        ) : route.page === "outils" ? (
          <Outils onSortir={() => (window.location.href = "/")} />
        ) : route.page === "methode" ? (
          <Methode />
        ) : route.page === "barometre" ? (
          <Barometre />
        ) : route.page === "pilotage" ? (
          <Pilotage />
        ) : route.page === "apropos" ? (
          <APropos />
        ) : route.page === "aide" ? (
          <Aide />
        ) : route.page === "progression" ? (
          <Progression jeton={route.serial} />
        ) : route.serial === "" ? (
          <AttestationEntree />
        ) : (
          <Attestation serial={route.serial} />
        )}
        <Footer />
        <ConsentementMeta />
        {/*
          La fenêtre de paiement, sur la seule page hors parcours qui vend.

          Elle n'était montée que dans le parcours salarié : sur `/dirigeant`,
          « Obtenir le rapport de mon entreprise — 15 000 F » posait son état
          et ne faisait rien du tout. Un bouton d'achat mort sur l'écran qui
          porte le prix le plus élevé du produit — trouvé en déroulant le
          parcours au navigateur, pas en relisant le code.
        */}
        {(route.page === "dirigeant" || route.page === "progression") && <PayModal />}
      </AppProvider>
    );
  }

  return (
    <AppProvider price={CONFIG.price}>
      <Routeur />
    </AppProvider>
  );
}

/** `/dirigeant` ouvert, ou sa fermeture si l'exploitation a fermé l'axe (09/10/2026). */
function PageDirigeant() {
  const { dirigeantActif, reglagesLus } = useApp();
  // Rien tant que l'on ne sait pas si l'axe est ouvert : le formulaire monté
  // puis retiré comptait une entrée dirigeant sur un axe fermé.
  if (!reglagesLus) return null;
  return dirigeantActif ? (
    <Dirigeant onSortir={() => window.location.assign("/")} />
  ) : (
    <DirigeantFerme />
  );
}
